import useLookupStore from "@/lookups/stores/lookups";
import useSiteStore from "@/sites/stores/sites";
import AutocompleteField from "@/components/AutocompleteField";
import SelectField from "@/components/SelectField";
import DateField from "@/components/DateField";
import { useMemo } from "react";
import { Divider } from "antd";
import { queries, spacing } from "@/config/theme";
import { css } from "@emotion/react";
import TextField from "@/components/TextField";
import { createFormElement, FormElement } from "@/types/Form";
import CheckboxField from "@/components/CheckboxField";
import { DEFAULT_SITE_NAME } from "@/reports/config/sites";
import { useLocation, useParams } from "react-router-dom";
import { transformRecordsToOptions } from "@/lookups/utils/transform";
import { NANAIMO } from "@/reports/config/locations";
import FieldRenderer, {
  checkboxContainerStyles,
  FieldWrapper,
  FormSubtitle,
  fullGridStyles,
} from "../ReportForm/utils";

const CommentFieldCss = css`
  grid-column: 1 / -1;
  // half of screen size
  @media (${queries.laptopAndUp}) {
    grid-column: 1 / 5;
  }
`;

type Props = {
  disabled?: boolean;
};

const useFields = () => {
  const { location } = useParams();
  const routeLocation = useLocation();

  const { lookups } = useLookupStore();
  const { sites } = useSiteStore();
  const uniqueSites = useMemo(
    () => [...new Set(sites.map((site) => site.SiteName))],
    [sites]
  );

  return useMemo<FormElement[]>(
    () => [
      createFormElement(SelectField, {
        label: "Site",
        name: "vCustomer",
        placeholder: "Site",
        options: [
          {
            label: DEFAULT_SITE_NAME,
            value: DEFAULT_SITE_NAME,
          },
        ].concat(
          uniqueSites.map((siteName) => ({
            label: siteName,
            value: siteName,
          }))
        ),
        required: true,
        showSearch: true,
      }),
      createFormElement(TextField, {
        label: location === NANAIMO ? "NV #" : "VV #",
        name: "vVVNumber",
        inputMode: "decimal",
        transformInput: (value: string, previousValue = "") => {
          // Remove any existing hyphens
          let targetValue = value.replace(/-/g, "");

          // Remove hyphen if user is deleting
          if (value.length === 2 && previousValue.length === 3) {
            targetValue = targetValue.slice(0, 1);
          }

          // Insert a hyphen after the 2nd character
          if (targetValue.length >= 2) {
            targetValue = `${targetValue.slice(0, 2)}-${targetValue.slice(2)}`;
          }
          return targetValue;
        },
        autoFocus: routeLocation.state?.fromDuplicate ?? false,
      }),
      createFormElement(DateField, {
        label: "Date",
        name: "vDate",
      }),
      createFormElement(TextField, {
        label: "PO #",
        name: "vPONumber",
      }),
      createFormElement(TextField, {
        label: "WO #",
        name: "vWONumber",
      }),
      createFormElement(AutocompleteField, {
        label: "Type",
        name: "vType",
        options: transformRecordsToOptions(lookups.ManualValveRepairReportType),
        showSearch: true,
      }),
      createFormElement(AutocompleteField, {
        label: "Make",
        name: "vManufacturer",
        options: transformRecordsToOptions(
          lookups.ManualValveRepairReportManufacturer
        ),
        showSearch: true,
      }),
      createFormElement(TextField, {
        label: "Model #",
        name: "vModelNumber",
      }),
      createFormElement(AutocompleteField, {
        label: "Body",
        name: "vBody",
        options: transformRecordsToOptions(lookups.ManualValveRepairReportBody),
        showSearch: true,
      }),
      createFormElement(AutocompleteField, {
        label: "Seat Trim",
        name: "vSeatTrim",
        options: transformRecordsToOptions(
          lookups.ManualValveRepairReportSeatTrim
        ),
        showSearch: true,
      }),
      createFormElement(AutocompleteField, {
        label: "Disc Trim",
        name: "vDiscTrim",
        options: transformRecordsToOptions(
          lookups.ManualValveRepairReportDiscTrim
        ),
        showSearch: true,
      }),
      createFormElement(TextField, {
        label: "Size",
        name: "vValveSize",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Pressure",
        name: "vPressure",
        type: "number",
        inputMode: "decimal",
      }),
      createFormElement(TextField, {
        label: "Service",
        name: "vService",
      }),
      createFormElement(FormSubtitle, {
        elementContainerCss: fullGridStyles,
        marginTop: `-${spacing.xl}`,
        children: "",
        key: "Line Break",
      }),
      createFormElement(CheckboxField, {
        label: "Gear OP",
        name: "vGearOP",
        elementContainerCss: checkboxContainerStyles,
      }),
      createFormElement(CheckboxField, {
        label: "Actuated",
        name: "vActuated",
        elementContainerCss: checkboxContainerStyles,
      }),
      createFormElement(CheckboxField, {
        label: "Pressure Seal",
        name: "vPressureSeal",
        elementContainerCss: checkboxContainerStyles,
      }),
      createFormElement(CheckboxField, {
        label: "Bypass",
        name: "vBypass",
        elementContainerCss: checkboxContainerStyles,
      }),
    ],
    [location, lookups, routeLocation.state?.fromDuplicate, uniqueSites]
  );
};

const ManualValveData = ({ disabled }: Props) => {
  const fields = useFields();
  return (
    <>
      <FieldWrapper>
        <FieldRenderer fields={fields} disabled={disabled} />
      </FieldWrapper>
      <Divider />
      <FieldWrapper>
        <FieldRenderer
          fields={[
            createFormElement(TextField, {
              label: "Valve Condition",
              name: "valveCondition",
              type: "textarea",
              rows: 6,
              elementContainerCss: CommentFieldCss,
            }),
          ]}
          disabled={disabled}
        />
      </FieldWrapper>
    </>
  );
};

export default ManualValveData;
